import { useState } from "react";
import { Loader2, FileText, MailCheck, Mail } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { api, BACKEND_URL } from "../../lib/api";
import AdminTableToolbar from "./AdminTableToolbar";
import useAdminInfiniteList from "../../hooks/useAdminInfiniteList";
import { exportRowsToCSV } from "../../lib/csvExport";
import { toast } from "sonner";
import CertificateViewer from "../CertificateViewer";

const GOAL_LABEL = {
  living_tanakh: "תוריא",
  yeshivot: "ישיבות",
  charity: "צדקה",
};
const DEDICATION_LABEL = {
  memory: "לעילוי נשמת",
  honor: "להצלחת",
  healing: "לרפואת",
  none: "ללא",
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("he-IL", {
      day: "numeric",
      month: "short",
      year: "2-digit",
    });
  } catch {
    return "—";
  }
}

function amountDisplay(amount, currency) {
  const sym = (currency || "ILS").toUpperCase() === "USD" ? "$" : "₪";
  return `${sym}${amount}`;
}

export default function AdminDonorsTab() {
  const [q, setQ] = useState("");
  const [goal, setGoal] = useState("all");
  const [dedicationType, setDedicationType] = useState("all");
  const [includeDummy, setIncludeDummy] = useState(false);
  const [resending, setResending] = useState({}); // { [donation_id]: true }
  // URL of the certificate currently being viewed inside the in-app
  // <CertificateViewer> overlay. `null` keeps the overlay closed; an
  // absolute URL pops it open. We avoid `target="_blank"` because the
  // browser's native PDF viewer has no obvious "back" affordance, and
  // donors / admins kept getting stranded there.
  const [certUrl, setCertUrl] = useState(null);

  // The donors endpoint doesn't natively support free-text search,
  // so we pass q but the backend will ignore it; we client-side filter
  // the returned rows. This is cheap because the page size is 20.
  const { items, total, loading, hasMore, sentinelRef } = useAdminInfiniteList({
    fetchPage: api.adminDonors,
    params: {
      goal: goal === "all" ? undefined : goal,
      dedication_type: dedicationType === "all" ? undefined : dedicationType,
      include_dummy: includeDummy,
    },
  });

  // Client-side search filter on the items we already fetched.
  const filtered = q
    ? items.filter((d) => {
        const hay = `${d.name || ""} ${d.email || ""}`.toLowerCase();
        return hay.includes(q.toLowerCase());
      })
    : items;

  const handleExport = () => {
    exportRowsToCSV({
      filename: `torahya-donors-${new Date().toISOString().slice(0, 10)}.csv`,
      columns: [
        { key: "name", label: "שם" },
        { key: "email", label: "אימייל" },
        { key: "amount", label: "סכום", fmt: (r) => amountDisplay(r.amount, r.currency) },
        { key: "goal", label: "מטרה", fmt: (r) => GOAL_LABEL[r.goal] || r.goal || "—" },
        {
          key: "dedication_type",
          label: "סוג הקדשה",
          fmt: (r) => DEDICATION_LABEL[r.dedication_type] || "—",
        },
        { key: "dedication_text", label: "הקדשה" },
        { key: "receipt_number", label: "מס' קבלה" },
        { key: "created_at", label: "תאריך", fmt: (r) => formatDate(r.created_at) },
      ],
      rows: filtered,
    });
  };

  const handleResendReceipt = async (donation_id) => {
    if (resending[donation_id]) return;
    setResending((s) => ({ ...s, [donation_id]: true }));
    try {
      const res = await api.adminResendReceipt(donation_id);
      if (res?.ok) {
        toast.success("הקבלה נשלחה במייל לתורם");
      } else {
        toast.error("שליחת המייל נכשלה — בדוק יומן שגיאות Resend");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "שליחת הקבלה נכשלה");
    } finally {
      setResending((s) => ({ ...s, [donation_id]: false }));
    }
  };

  /** Receipt cell for a single row.
   *
   *  Two visually distinct actions live side-by-side:
   *    • **PDF** — opens the certificate. Every completed donation now
   *      has one (backend renders a general-receipt PDF when no
   *      dedication exists), so this is always shown.
   *    • **שלח שוב** — re-emails the receipt to the donor. Only
   *      relevant when we actually have a donor email on file;
   *      hidden for anonymous donations (yet they STILL get the PDF
   *      button so the admin can inspect what was generated).
   */
  const renderReceiptCell = (d) => {
    const pdfBtn = d.certificate_url ? (
      <button
        type="button"
        onClick={() => setCertUrl(`${BACKEND_URL}${d.certificate_url}`)}
        className="text-gold hover:text-gold/80 inline-flex items-center gap-1 text-xs"
        data-testid={`donor-cert-${d.donation_id}`}
        title="הצגת תעודת התרומה"
      >
        <FileText className="h-3.5 w-3.5" strokeWidth={1.5} />
        PDF
      </button>
    ) : null;

    if (!d.has_donor_email) {
      // Anonymous donation: PDF only (no email to resend to).
      return (
        <div className="flex items-center gap-3" data-testid={`donor-receipt-anon-${d.donation_id}`}>
          {pdfBtn || (
            <span className="text-[10px] text-muted-foreground">תרומה אנונימית</span>
          )}
        </div>
      );
    }

    const sent = !!d.email_sent_at;
    const isLoading = !!resending[d.donation_id];
    const resendBtn = (
      <button
        type="button"
        onClick={() => handleResendReceipt(d.donation_id)}
        disabled={isLoading}
        data-testid={`donor-resend-${d.donation_id}`}
        title={sent ? "הקבלה כבר נשלחה — לחץ לשליחה חוזרת" : "שליחת קבלה במייל"}
        className="inline-flex items-center gap-1 text-[11px] text-foreground/80 hover:text-gold disabled:opacity-50"
      >
        {isLoading ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
        ) : sent ? (
          <MailCheck className="h-3.5 w-3.5 text-emerald-600" strokeWidth={1.5} />
        ) : (
          <Mail className="h-3.5 w-3.5 text-amber-600" strokeWidth={1.5} />
        )}
        {sent ? "שלח שוב" : "שלח קבלה"}
      </button>
    );
    return (
      <div className="flex items-center gap-3">
        {pdfBtn}
        {resendBtn}
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="admin-donors">
      <AdminTableToolbar
        q={q}
        onSearch={setQ}
        searchPlaceholder="חיפוש לפי שם תורם או אימייל…"
        includeDummy={includeDummy}
        onToggleDummy={setIncludeDummy}
        onExportCSV={handleExport}
        exportDisabled={filtered.length === 0}
        testIdPrefix="admin-donors"
      >
        <Select value={goal} onValueChange={setGoal}>
          <SelectTrigger className="w-[140px]" data-testid="admin-donors-goal-filter">
            <SelectValue placeholder="מטרה" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">כל המטרות</SelectItem>
            <SelectItem value="living_tanakh">תוריא</SelectItem>
            <SelectItem value="yeshivot">ישיבות</SelectItem>
            <SelectItem value="charity">צדקה</SelectItem>
          </SelectContent>
        </Select>
        <Select value={dedicationType} onValueChange={setDedicationType}>
          <SelectTrigger className="w-[160px]" data-testid="admin-donors-dedication-filter">
            <SelectValue placeholder="הקדשה" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">כל ההקדשות</SelectItem>
            <SelectItem value="memory">לעילוי נשמת</SelectItem>
            <SelectItem value="honor">להצלחת</SelectItem>
            <SelectItem value="healing">לרפואת</SelectItem>
            <SelectItem value="none">ללא הקדשה</SelectItem>
          </SelectContent>
        </Select>
      </AdminTableToolbar>

      <div className="text-xs text-muted-foreground">
        מציג {filtered.length} מתוך {total}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block rounded-md border border-border/60 overflow-hidden">
        <table className="w-full text-sm" data-testid="admin-donors-table">
          <thead className="bg-card/40 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-right p-3">תורם</th>
              <th className="text-right p-3">סכום</th>
              <th className="text-right p-3">מטרה</th>
              <th className="text-right p-3">הקדשה</th>
              <th className="text-right p-3">תאריך</th>
              <th className="text-right p-3 w-20">תעודה</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((d) => (
              <tr
                key={d.donation_id}
                className="border-t border-border/30 hover:bg-card/40"
                data-testid={`donor-row-${d.donation_id}`}
              >
                <td className="p-3">
                  <div className="font-heading text-foreground">
                    {d.name || "אנונימי"}
                  </div>
                  <div className="text-xs text-muted-foreground" dir="ltr">
                    {d.email || "—"}
                  </div>
                </td>
                <td className="p-3">
                  <span className="font-heading text-base text-foreground">
                    {amountDisplay(d.amount, d.currency)}
                  </span>
                  {d.is_recurring && (
                    <span className="text-xs text-gold/80 mr-1">חוזרת</span>
                  )}
                </td>
                <td className="p-3 text-muted-foreground">
                  {GOAL_LABEL[d.goal] || d.goal || "—"}
                </td>
                <td className="p-3 text-muted-foreground">
                  {d.dedication_type && d.dedication_type !== "none" ? (
                    <>
                      <div className="text-xs text-gold/80">
                        {DEDICATION_LABEL[d.dedication_type]}
                      </div>
                      <div className="text-sm truncate max-w-[180px]">
                        {d.dedication_text}
                      </div>
                    </>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="p-3 text-muted-foreground">{formatDate(d.created_at)}</td>
                <td className="p-3">
                  {renderReceiptCell(d)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {filtered.map((d) => (
          <div
            key={d.donation_id}
            className="rounded-md border border-border/60 bg-card/40 p-4"
            data-testid={`donor-card-${d.donation_id}`}
          >
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="min-w-0 flex-1">
                <div className="font-heading text-foreground truncate">
                  {d.name || "אנונימי"}
                </div>
                <div className="text-xs text-muted-foreground truncate" dir="ltr">
                  {d.email || "—"}
                </div>
              </div>
              <div className="font-heading text-base text-foreground whitespace-nowrap">
                {amountDisplay(d.amount, d.currency)}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
              <div>{GOAL_LABEL[d.goal] || d.goal || "—"}</div>
              <div>{formatDate(d.created_at)}</div>
              {d.dedication_type && d.dedication_type !== "none" && (
                <div className="col-span-2 text-gold/80">
                  {DEDICATION_LABEL[d.dedication_type]}: {d.dedication_text}
                </div>
              )}
            </div>
            <div className="mt-2">
              {renderReceiptCell(d)}
            </div>
          </div>
        ))}
      </div>

      <div ref={sentinelRef} className="h-8 flex items-center justify-center">
        {loading && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />}
        {!loading && !hasMore && items.length > 0 && (
          <p className="text-xs text-muted-foreground">— הגעת לסוף הרשימה —</p>
        )}
        {!loading && filtered.length === 0 && (
          <p className="text-sm text-muted-foreground py-6">לא נמצאו תורמים.</p>
        )}
      </div>

      <CertificateViewer
        open={!!certUrl}
        url={certUrl}
        onClose={() => setCertUrl(null)}
        testid="admin-cert-viewer"
      />
    </div>
  );
}
